import React, { useEffect, useMemo, useState, useCallback } from "react";
import { CalendarRange, Loader2, Sun } from "lucide-react";
import { toast } from "sonner";
import { getEntries, getAheadSummary, updateEntry } from "../lib/api";
import { deleteWithUndo } from "../lib/items";
import { TypeBadge, DomainBadge, OwnerBadge } from "../components/Badges";
import ItemActionsMenu from "../components/ItemActionsMenu";
import ItemDetailSheet from "../components/ItemDetailSheet";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { startOfToday, startOfTomorrow } from "../lib/resurface";
import { SCHEDULE_TYPES, aheadForward } from "../lib/browse";

const SCOPES = [
  { key: "mine", label: "Mine" },
  { key: "partner", label: "Partner" },
  { key: "both", label: "Both" },
];
const TIMEFRAMES = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "all", label: "All ahead" },
];

function dayKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayHeading(d) {
  const today = startOfToday();
  const diff = Math.round((new Date(d).setHours(0, 0, 0, 0) - today) / 86400000);
  const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
  const datePart = d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  if (diff === 0) return { lead: "Today", sub: datePart };
  if (diff === 1) return { lead: "Tomorrow", sub: datePart };
  return { lead: weekday, sub: datePart };
}

function timeLabel(iso) {
  const d = new Date(iso);
  if (d.getHours() === 0 && d.getMinutes() === 0) return null;
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

// Compact human label for the AI payload.
function whenLabel(iso) {
  const d = new Date(iso);
  const { lead } = dayHeading(d);
  const t = timeLabel(iso);
  const base = lead === "Today" || lead === "Tomorrow"
    ? lead
    : d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  return t ? `${base} ${t}` : base;
}

function AheadCard({ entry, onChanged, onOpen }) {
  const t = entry.suggested_datetime ? timeLabel(entry.suggested_datetime) : null;
  const isWindow = entry.timing_status === "window";
  return (
    <div
      className="heldall-card bg-[var(--surface)] border border-[var(--line)] p-4 md:p-5 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_8px_28px_rgba(44,40,37,0.09)]"
      data-testid={`ahead-item-${entry.id}`}
    >
      <div className="flex items-start gap-4">
        {t && (
          <div className="shrink-0 w-16 pt-0.5">
            <span className="text-[13px] font-medium text-[var(--ink3)]">{t}</span>
          </div>
        )}
        <button
          type="button"
          data-testid={`open-detail-${entry.id}`}
          onClick={() => onOpen && onOpen(entry.id)}
          className="min-w-0 flex-1 text-left group/title"
        >
          <p className="text-[var(--ink)] text-[15px] font-medium leading-snug group-hover/title:underline decoration-[var(--pine-line)] underline-offset-2">{entry.interpreted_summary}</p>
          {isWindow && entry.timing_phrase && (
            <p className="text-[13px] text-[var(--ink3)] mt-1" data-testid={`ahead-window-label-${entry.id}`}>
              Held for {entry.timing_phrase} · no exact date
            </p>
          )}
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            <TypeBadge type={entry.type} subtle />
            <DomainBadge domain={entry.domain} subtle />
            {entry.owner !== "me" && <OwnerBadge owner={entry.owner} name={entry.assignee_name} />}
          </div>
        </button>
        <ItemActionsMenu entry={entry} onUpdated={onChanged} />
      </div>
    </div>
  );
}

export default function Ahead() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("both");
  const [timeframe, setTimeframe] = useState("week");
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const reload = useCallback(() => {
    getEntries().then(setEntries).finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const editEntry = (id, changes) =>
    updateEntry(id, { ...changes, tz_offset: -new Date().getTimezoneOffset() })
      .then(() => { reload(); toast.success("Saved."); })
      .catch(() => toast.error("Couldn't save that."));

  const handleDelete = (id) => {
    setDetailId(null);
    deleteWithUndo(id, { onDeleted: reload, onRestored: reload });
  };

  // Forward-looking, schedule-worthy active items only.
  const base = useMemo(() => {
    const inScope = (e) => {
      // A household / "Both" item (shared, not handed to one person) belongs to everyone,
      // so it appears in Mine, Partner, and Both. Handoffs keep their owner semantics.
      const isBoth = e.visibility === "shared" && !e.is_handoff;
      if (isBoth) return true;
      if (scope === "mine") return e.owner === "me";
      if (scope === "partner") return e.owner === "partner";
      return true;
    };
    return entries.filter(
      (e) => e.status === "ACTIVE" && SCHEDULE_TYPES.has(e.type) && inScope(e)
    );
  }, [entries, scope]);

  const datedAll = useMemo(() => aheadForward(base).dated, [base]);

  const dated = useMemo(() => {
    if (timeframe === "all") return datedAll;
    const upper = timeframe === "today"
      ? startOfTomorrow()
      : new Date(startOfToday().getTime() + 7 * 86400000);
    return datedAll.filter((e) => new Date(e.suggested_datetime) < upper);
  }, [datedAll, timeframe]);

  const undated = useMemo(() => aheadForward(base).undated, [base]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const e of dated) {
      const d = new Date(e.suggested_datetime);
      const k = dayKey(d);
      if (!map.has(k)) map.set(k, { date: d, items: [] });
      map.get(k).items.push(e);
    }
    return Array.from(map.values());
  }, [dated]);

  const payload = useMemo(
    () => dated.map((e) => ({
      summary: e.interpreted_summary,
      when: whenLabel(e.suggested_datetime),
      owner: e.owner === "me" ? "me" : (e.assignee_name || e.owner),
    })),
    [dated]
  );

  // AI summary refreshes when the visible dated set changes.
  useEffect(() => {
    if (loading) return;
    if (payload.length === 0) {
      setSummary("");
      return;
    }
    let alive = true;
    setSummaryLoading(true);
    getAheadSummary(payload, scope)
      .then((r) => { if (alive) setSummary(r.status === "ok" ? r.summary : ""); })
      .catch(() => alive && setSummary(""))
      .finally(() => alive && setSummaryLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(payload), scope, loading]);

  const fallbackLine = `${dated.length} ${dated.length === 1 ? "thing" : "things"} on the way${
    timeframe === "today" ? " today" : timeframe === "week" ? " this week" : ""
  }.`;

  const chip = (active) =>
    `inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all duration-200 ${
      active ? "bg-[var(--surface)] text-[var(--ink)] shadow-[0_2px_8px_rgba(0,0,0,0.06)]" : "text-[var(--ink3)] hover:text-[var(--ink2)]"
    }`;

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="ahead-page">
      <div className="mb-7">
        <h1 className="font-display text-4xl tracking-tight font-medium text-[var(--ink)]">Ahead</h1>
        <p className="text-[var(--ink3)] mt-2">What's coming, in the order it arrives.</p>
      </div>

      {/* Layer 1 — calm descriptive summary */}
      <div className="rounded-2xl bg-[var(--line2)] border border-[var(--line)] p-5 md:p-6 mb-8" data-testid="ahead-summary-card">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-[var(--pine)] flex items-center justify-center shrink-0 shadow-[0_6px_18px_rgba(44,90,77,0.22)]">
            <Sun size={18} strokeWidth={1.9} className="text-[var(--on-pine)]" />
          </div>
          <div className="min-w-0 flex-1">
            {loading || summaryLoading ? (
              <div className="flex items-center gap-2 text-[var(--ink3)] text-[15px]" data-testid="ahead-summary-loading">
                <Loader2 size={15} className="animate-spin" /> Reading what's ahead…
              </div>
            ) : (
              <p className="text-[var(--ink2)] text-[15px] md:text-base leading-relaxed font-medium" data-testid="ahead-summary">
                {summary || fallbackLine}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Layer 2 — filters */}
      <div className="flex flex-wrap items-center gap-3 mb-8">
        <div className="inline-flex items-center gap-1 rounded-full bg-[var(--line2)] p-1" data-testid="ahead-scope-tabs">
          {SCOPES.map((s) => (
            <button key={s.key} data-testid={`ahead-scope-${s.key}`} className={chip(scope === s.key)} onClick={() => setScope(s.key)}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-1 rounded-full bg-[var(--line2)] p-1" data-testid="ahead-timeframe-tabs">
          {TIMEFRAMES.map((tf) => (
            <button key={tf.key} data-testid={`ahead-timeframe-${tf.key}`} className={chip(timeframe === tf.key)} onClick={() => setTimeframe(tf.key)}>
              {tf.label}
            </button>
          ))}
        </div>
      </div>

      {/* Layer 3 — chronological day groups */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-[var(--pine)]" data-testid="ahead-loading">
          <Loader2 className="animate-spin" size={26} />
        </div>
      ) : dated.length === 0 && undated.length === 0 ? (
        <div className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] p-12 text-center" data-testid="ahead-empty">
          <CalendarRange className="mx-auto text-[var(--pine-line)] mb-3" size={30} strokeWidth={1.4} />
          <p className="text-[var(--ink3)] max-w-sm mx-auto leading-relaxed">
            Nothing scheduled {timeframe === "today" ? "for today" : timeframe === "week" ? "this week" : "ahead"} yet. Capture something with a time and it'll line up here.
          </p>
        </div>
      ) : (
        (() => {
          const groupDiff = (d) => Math.round((new Date(d).setHours(0, 0, 0, 0) - startOfToday()) / 86400000);
          const ttGroups = groups.filter((g) => groupDiff(g.date) <= 1);
          const weekGroups = groups.filter((g) => { const df = groupDiff(g.date); return df >= 2 && df <= 7; });
          const laterGroups = groups.filter((g) => groupDiff(g.date) > 7);
          const countOf = (gs) => gs.reduce((n, g) => n + g.items.length, 0);
          const renderGroups = (gs) => (
            <div className="space-y-7">
              {gs.map((g) => {
                const { lead, sub } = dayHeading(g.date);
                return (
                  <div key={dayKey(g.date)} data-testid={`ahead-day-${dayKey(g.date)}`}>
                    <div className="flex items-baseline gap-2 mb-3">
                      <h3 className="text-base font-semibold text-[var(--ink2)]">{lead}</h3>
                      <span className="text-sm text-[var(--ink4)]">{sub}</span>
                      <span className="text-xs font-semibold text-[var(--ink4)] bg-[var(--line2)] rounded-full px-2 py-0.5 ml-1">{g.items.length}</span>
                    </div>
                    <div className="space-y-3">
                      {g.items.map((e) => <AheadCard key={e.id} entry={e} onChanged={reload} onOpen={setDetailId} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          );
          const laterCount = countOf(laterGroups) + undated.length;
          return (
            <div className="space-y-9">
              {ttGroups.length > 0 && (
                <CollapsibleSection id="ahead-today" title="Today & tomorrow" count={countOf(ttGroups)} hint="What's next." collapsible={false}>
                  {renderGroups(ttGroups)}
                </CollapsibleSection>
              )}
              {weekGroups.length > 0 && (
                <CollapsibleSection id="ahead-week" title="This week" count={countOf(weekGroups)} hint="Coming up soon." collapsible defaultOpen>
                  {renderGroups(weekGroups)}
                </CollapsibleSection>
              )}
              {laterCount > 0 && (
                <CollapsibleSection id="ahead-later" title="Later" count={laterCount} hint="Quietly held until you're ready." collapsible defaultOpen={false}>
                  {laterGroups.length > 0 && renderGroups(laterGroups)}
                  {undated.length > 0 && (
                    <div className={laterGroups.length > 0 ? "mt-7" : ""} data-testid="ahead-undated-section">
                      <div className="flex items-baseline gap-2 mb-3">
                        <h3 className="text-base font-semibold text-[var(--ink3)]">No date yet</h3>
                        <span className="text-xs font-semibold text-[var(--ink4)] bg-[var(--line2)] rounded-full px-2 py-0.5">{undated.length}</span>
                      </div>
                      <div className="space-y-3">
                        {undated.map((e) => <AheadCard key={e.id} entry={e} onChanged={reload} onOpen={setDetailId} />)}
                      </div>
                    </div>
                  )}
                </CollapsibleSection>
              )}
            </div>
          );
        })()
      )}

      <ItemDetailSheet
        entry={entries.find((e) => e.id === detailId) || null}
        open={!!detailId}
        onOpenChange={(o) => { if (!o) setDetailId(null); }}
        onChange={editEntry}
        onDelete={handleDelete}
      />
    </div>
  );
}
